import React, { ReactNode } from 'react';
import clsx from 'clsx';
import { Colors, FontSize, FontWeight, Spacing } from '@common/tokens';

/**
 * Selectable pill rendered in the {@link PlannerCalendar} week strip. Agnostic: the caller
 * supplies the label, an optional `indicator` slot, and an `alert` flag (a coral-border emphasis
 * the caller assigns meaning to, e.g. a flagged week).
 */
export function WeekPill({
  label,
  selected,
  indicator,
  alert,
  onClick,
}: {
  label: ReactNode;
  selected?: boolean;
  indicator?: ReactNode;
  alert?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      className={clsx('wk-week-pill', {
        'wk-week-pill--selected': selected,
        'wk-week-pill--alert': alert,
      })}
      aria-pressed={selected}
      onClick={onClick}
    >
      <span className="wk-week-pill__label">{label}</span>
      {indicator ? (
        <span className="wk-week-pill__indicator">{indicator}</span>
      ) : null}
      <style jsx>{`
        .wk-week-pill {
          display: inline-flex;
          align-items: center;
          gap: ${Spacing.sm}px;
          padding: ${Spacing.sm}px ${Spacing.md}px;
          border: 1.5px solid ${Colors.steelLighter};
          border-radius: 20px;
          background: transparent;
          cursor: pointer;
          outline: none;
          white-space: nowrap;
          transition:
            background 0.1s ease,
            border-color 0.1s ease;
        }
        .wk-week-pill:hover:not(.wk-week-pill--selected) {
          background: ${Colors.steelLightest};
          border-color: ${Colors.steelDarker};
        }
        .wk-week-pill:focus-visible {
          box-shadow: 0 0 0 2px ${Colors.elm};
        }
        .wk-week-pill--selected {
          background: ${Colors.elmHyperlight};
          border-color: ${Colors.elm};
        }
        /* Alert emphasis — coral border signals the caller's flagged state. */
        .wk-week-pill--alert {
          border-color: ${Colors.coral};
        }
        .wk-week-pill--selected.wk-week-pill--alert {
          border-color: ${Colors.coral};
          background: ${Colors.elmHyperlight};
        }
        .wk-week-pill__label {
          font-size: ${FontSize.sm};
          font-weight: ${FontWeight.medium};
          color: ${Colors.midnightRegular};
          line-height: 1;
        }
        .wk-week-pill--selected .wk-week-pill__label {
          color: ${Colors.elm};
        }
        .wk-week-pill__indicator {
          display: inline-flex;
          align-items: center;
        }
      `}</style>
    </button>
  );
}
